import React, { useState, useEffect, useCallback } from 'react';
import { RefreshCw, Search, Filter } from 'lucide-react';
import api from '../utils/api';

export default function EventsScreen() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [eventType, setEventType] = useState('');
  const [userIdFilter, setUserIdFilter] = useState('');
  const [limit, setLimit] = useState(100);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await api.getEvents({
        eventType: eventType || undefined,
        userId: userIdFilter || undefined,
        limit,
      });
      setEvents(data.events || data.data || data || []);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [eventType, userIdFilter, limit]);

  useEffect(() => {
    loadEvents();
  }, [loadEvents]);

  const formatDate = (dateStr) => {
    if (!dateStr) return '—';
    try {
      return new Date(dateStr).toLocaleString('ru-RU', {
        day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit', second: '2-digit',
      });
    } catch { return dateStr; }
  };

  const eventTypeColors = {
    order: 'bg-blue-500/20 text-blue-400',
    auth: 'bg-green-500/20 text-green-400',
    kyc: 'bg-purple-500/20 text-purple-400',
    payment: 'bg-amber-500/20 text-amber-400',
    error: 'bg-red-500/20 text-red-400',
    admin: 'bg-cyan-500/20 text-cyan-400',
  };

  const getEventColor = (type) => {
    if (!type) return 'bg-gray-500/20 text-gray-400';
    const key = Object.keys(eventTypeColors).find(k => type.toLowerCase().includes(k));
    return eventTypeColors[key] || 'bg-gray-500/20 text-gray-400';
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold text-white">События</h2>
        <button
          onClick={loadEvents}
          disabled={loading}
          className="p-2 bg-gray-800 rounded-lg border border-gray-700 text-gray-400 hover:text-white disabled:opacity-50"
        >
          <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-3 mb-4">
        <div className="relative flex-1 min-w-[150px]">
          <Filter size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Тип события..."
            value={eventType}
            onChange={e => setEventType(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <div className="relative flex-1 min-w-[150px]">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="User ID..."
            value={userIdFilter}
            onChange={e => setUserIdFilter(e.target.value)}
            className="w-full bg-gray-800 border border-gray-700 rounded-lg pl-9 pr-4 py-2 text-sm text-white placeholder-gray-500 focus:outline-none focus:border-purple-500"
          />
        </div>
        <select
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
          className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-purple-500"
        >
          <option value={50}>50</option>
          <option value={100}>100</option>
          <option value={200}>200</option>
          <option value={500}>500</option>
        </select>
      </div>

      {error && (
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 mb-4 text-red-400 text-sm">
          {error}
        </div>
      )}

      {/* Events list */}
      <div className="bg-gray-800 rounded-xl border border-gray-700/50 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-gray-400 text-xs border-b border-gray-700">
                <th className="text-left p-3">Время</th>
                <th className="text-left p-3">Тип</th>
                <th className="text-left p-3">User ID</th>
                <th className="text-left p-3">Детали</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                Array.from({ length: 8 }).map((_, i) => (
                  <tr key={i} className="border-t border-gray-700/50">
                    <td colSpan={4} className="p-3">
                      <div className="h-4 bg-gray-700 rounded animate-pulse" />
                    </td>
                  </tr>
                ))
              ) : events.length === 0 ? (
                <tr>
                  <td colSpan={4} className="p-8 text-center text-gray-500">
                    Нет событий
                  </td>
                </tr>
              ) : (
                events.map((event, i) => (
                  <tr key={event.id || i} className="border-t border-gray-700/50 hover:bg-gray-700/30">
                    <td className="p-3 text-gray-400 text-xs whitespace-nowrap">
                      {formatDate(event.timestamp || event.createdAt || event.created_at)}
                    </td>
                    <td className="p-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${getEventColor(event.type || event.event_type)}`}>
                        {event.type || event.event_type || '—'}
                      </span>
                    </td>
                    <td className="p-3 text-white text-xs font-mono">
                      {event.userId || event.user_id || '—'}
                    </td>
                    <td className="p-3 text-gray-300 text-xs max-w-xs truncate">
                      {event.details || event.message || event.description ||
                        (event.data ? JSON.stringify(event.data).slice(0, 80) : '—')}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {!loading && (
        <p className="text-xs text-gray-500 mt-3">
          Показано: {events.length} событий
        </p>
      )}
    </div>
  );
}
